import { useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSite, useSiteConfig, useSiteLogs, useSystem, useServers, useSiteHistory, useSiteSourceStatus } from "@/lib/hooks";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { StatusBadge } from "@/components/StatusBadge";
import { StackBadge } from "@/components/StackBadge";
import { CodeBlock } from "@/components/CodeBlock";
import { SiteConsole } from "@/components/SiteConsole";
import { SiteFiles } from "@/components/SiteFiles";
import { SiteRescueBar } from "@/components/SiteRescueBar";
import {
  Play, Square, RotateCcw, RefreshCw, Rocket, Trash2, ExternalLink,
  ScrollText, FileCode, KeyRound, ShieldAlert, Plus, ChevronLeft, UploadCloud,
  AlertCircle, CheckCircle2, History, XCircle, TerminalSquare, HardDrive, Cloud,
  Globe, Link2Off, Loader2, ArrowRight, ArrowRightLeft, Server as ServerIcon,
  Info
} from "lucide-react";
import { toast } from "sonner";
import useSWR from "swr";

export default function SiteDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { data: site, mutate: refetchSite } = useSite(id);
  const { data: config } = useSiteConfig(id);
  const { data: logsData } = useSiteLogs(id);
  const { data: sys } = useSystem();
  const { data: servers } = useServers();
  const { data: history } = useSiteHistory(id);
  const { data: sourceStatus, mutate: refetchSource } = useSiteSourceStatus(id);
  const fileInputRef = useRef(null);
  const [reuploadBusy, setReuploadBusy] = useState(false);

  if (!site) {
    return <div className="text-muted-foreground">Loading site…</div>;
  }

  const targetServer = (servers || []).find((s) => s.id === site.server_id);

  const fullHost =
    site.custom_domain || `${site.subdomain}.${sys?.base_domain || "emhost.local"}`;

  const doAction = async (path, ok) => {
    try {
      await api.post(`/sites/${site.id}/${path}`);
      toast.success(ok);
      refetchSite();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `${path} failed`);
    }
  };

  const del = async () => {
    try {
      await api.delete(`/sites/${site.id}`);
      toast.success("Deleted");
      nav("/sites");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "delete failed");
    }
  };

  const doReupload = async (file) => {
    if (!file) return;
    setReuploadBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/sites/${site.id}/reupload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Source uploaded — you can now Deploy");
      refetchSite();
      refetchSource();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "re-upload failed");
    } finally {
      setReuploadBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Link to="/sites" className="inline-flex items-center gap-1 hover:text-foreground">
          <ChevronLeft className="h-4 w-4" /> Sites
        </Link>
        <span>/</span>
        <span className="text-foreground truncate">{site.name}</span>
      </div>

      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight truncate">{site.name}</h1>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <a href={`https://${fullHost}`} target="_blank" rel="noreferrer"
               className="font-mono text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
              {fullHost} <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <StackBadge type={site.stack_type} />
            <StatusBadge status={site.status} testid="site-status-badge" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="site-actions-deploy-button" variant="default" className="gap-2"
                  onClick={() => doAction("deploy", "Deploy started")}>
            <Rocket className="h-4 w-4" /> Deploy
          </Button>
          <Button data-testid="site-actions-start-button" variant="secondary" className="gap-2"
                  onClick={() => doAction("start", "Started")}>
            <Play className="h-4 w-4" /> Start
          </Button>
          <Button data-testid="site-actions-stop-button" variant="secondary" className="gap-2"
                  onClick={() => doAction("stop", "Stopped")}>
            <Square className="h-4 w-4" /> Stop
          </Button>
          <Button data-testid="site-actions-restart-button" variant="secondary" className="gap-2"
                  onClick={() => doAction("restart", "Restarting…")}>
            <RotateCcw className="h-4 w-4" /> Restart
          </Button>
        </div>
      </header>

      <Tabs defaultValue="overview" data-testid="site-tabs">
        <TabsList className="surface-1 border border-border rounded-xl p-1 flex-wrap h-auto">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="env"><KeyRound className="h-4 w-4 mr-1.5" />Env</TabsTrigger>
          <TabsTrigger value="console"><TerminalSquare className="h-4 w-4 mr-1.5" />Console</TabsTrigger>
          <TabsTrigger value="files"><HardDrive className="h-4 w-4 mr-1.5" />Files</TabsTrigger>
          <TabsTrigger value="logs"><ScrollText className="h-4 w-4 mr-1.5" />Logs</TabsTrigger>
          <TabsTrigger value="config"><FileCode className="h-4 w-4 mr-1.5" />Config</TabsTrigger>
          <TabsTrigger value="history"><History className="h-4 w-4 mr-1.5" />History</TabsTrigger>
          <TabsTrigger value="danger"><ShieldAlert className="h-4 w-4 mr-1.5" />Danger</TabsTrigger>
        </TabsList>

        {site.status === "failed" && (
          <div
            data-testid="site-failure-banner"
            className="mt-4 border rounded-xl p-4"
            style={{ background: "hsl(0 60% 12%)", borderColor: "hsl(0 60% 30%)" }}
          >
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-red-200">Last deploy failed</div>
                <div className="text-sm text-red-200/80 mt-0.5">
                  Check the Logs tab for details.{" "}
                  {targetServer
                    ? <>Verify the deploy target <code className="font-mono">{targetServer.name}</code> is reachable and bootstrapped.</>
                    : "This site is targeting Simulation mode — set a real server in the Env tab to deploy for real."}
                </div>
              </div>
              <Button size="sm" className="gap-1.5"
                      onClick={() => doAction("deploy", "Retrying deploy…")}
                      data-testid="site-failure-retry-button">
                <Rocket className="h-3.5 w-3.5" /> Retry
              </Button>
            </div>
          </div>
        )}

        {sourceStatus?.needs_reupload && (
          <div
            data-testid="site-source-missing-banner"
            className="mt-4 border rounded-xl p-4"
            style={{ background: "hsl(35 92% 12%)", borderColor: "hsl(35 92% 30%)" }}
          >
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" style={{ color: "hsl(43 96% 60%)" }} />
              <div className="flex-1 min-w-0">
                <div className="font-medium" style={{ color: "hsl(33 100% 92%)" }}>Source files missing</div>
                <div className="text-sm mt-0.5" style={{ color: "hsl(33 100% 82%)" }}>
                  {sourceStatus.hint || "The emhost panel workspace was wiped (usually by a container restart) and no MongoDB backup exists."}
                  {" "}Re-upload the ZIP for this site to deploy again. Your subdomain, env, and deploy history are preserved.
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".zip"
                className="hidden"
                data-testid="site-reupload-file-input"
                onChange={(e) => doReupload(e.target.files?.[0])}
              />
              <Button
                size="sm"
                className="gap-1.5 shrink-0"
                disabled={reuploadBusy}
                data-testid="site-reupload-button"
                onClick={() => fileInputRef.current?.click()}
              >
                <UploadCloud className="h-3.5 w-3.5" />
                {reuploadBusy ? "Uploading…" : "Re-upload ZIP"}
              </Button>
            </div>
          </div>
        )}
        {sourceStatus && !sourceStatus.needs_reupload && !sourceStatus.present && sourceStatus.mongo_persisted && (
          <div
            data-testid="site-source-mongo-backup-banner"
            className="mt-4 border rounded-xl p-4"
            style={{ background: "hsl(210 40% 12%)", borderColor: "hsl(210 40% 26%)" }}
          >
            <div className="flex items-start gap-3">
              <Cloud className="h-5 w-5 shrink-0 mt-0.5" style={{ color: "hsl(210 40% 78%)" }} />
              <div className="flex-1 min-w-0">
                <div className="font-medium" style={{ color: "hsl(210 40% 92%)" }}>
                  Source stored in MongoDB backup ({sourceStatus.mongo_size} bytes)
                </div>
                <div className="text-sm mt-0.5" style={{ color: "hsl(210 40% 82%)" }}>
                  {sourceStatus.hint || "The local workspace is missing but a MongoDB copy exists — the next Deploy will auto-restore the source and continue."}
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".zip"
                className="hidden"
                onChange={(e) => doReupload(e.target.files?.[0])}
              />
              <Button
                size="sm"
                variant="secondary"
                className="gap-1.5 shrink-0"
                disabled={reuploadBusy}
                data-testid="site-reupload-button-optional"
                onClick={() => fileInputRef.current?.click()}
              >
                <UploadCloud className="h-3.5 w-3.5" />
                {reuploadBusy ? "Uploading…" : "Replace ZIP"}
              </Button>
            </div>
          </div>
        )}

        {site.server_id && (
          <SiteRescueBar site={site} onChanged={refetchSite} />
        )}

        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="p-5 md:col-span-2 border-border">
              <h2 className="text-lg font-semibold">Details</h2>
              <div className="mt-3 space-y-2 text-sm">
                <Row k="Site ID" v={site.id} mono />
                <Row k="Slug" v={site.slug} mono />
                <Row k="Subdomain" v={`${site.subdomain}.${sys?.base_domain || "emhost.local"}`} mono />
                <Row k="Custom domain" v={site.custom_domain || "—"} mono />
                <Row k="Deploy target" v={targetServer ? `${targetServer.name} (${targetServer.username}@${targetServer.host}:${targetServer.port})` : "Simulation (no server)"} mono />
                <Row k="Stack" v={site.stack_type} />
                <Row k="Status" v={site.status} />
                <Row k="Created" v={site.created_at ? new Date(site.created_at).toLocaleString() : "—"} />
                <Row k="Last deploy" v={site.last_deploy_at ? new Date(site.last_deploy_at).toLocaleString() : "—"} />
              </div>
            </Card>

            <Card className="p-5 border-border">
              <h2 className="text-lg font-semibold">Detection</h2>
              <div className="mt-3 text-sm text-muted-foreground">{site.detect_notes || "—"}</div>
              <ul className="mt-3 space-y-1 text-sm">
                {(site.detect_evidence || []).map((e, i) => (
                  <li key={i} className="font-mono text-[12.5px]">• {e}</li>
                ))}
              </ul>
              <div className="mt-4">
                <RedeployButton siteId={site.id} onDone={refetchSite} />
              </div>
            </Card>

            <Card className="p-5 md:col-span-3 border-border">
              <CustomDomainCard site={site} onChanged={refetchSite} />
            </Card>

            {site.server_id && (
              <Card className="p-5 md:col-span-3 border-border">
                <MigrateSiteCard site={site} onChanged={refetchSite} />
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="env" className="mt-4">
          <EnvTab site={site} onSaved={refetchSite} />
        </TabsContent>

        <TabsContent value="console" className="mt-4">
          <SiteConsole site={site} />
        </TabsContent>

        <TabsContent value="files" className="mt-4">
          <SiteFiles site={site} />
        </TabsContent>

        <TabsContent value="logs" className="mt-4">
          <Card data-testid="site-logs-panel" className="p-0 border-border overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border surface-1">
              <div className="flex items-center gap-2 text-sm">
                <ScrollText className="h-4 w-4" />
                Container logs
              </div>
              <div className="text-xs text-muted-foreground">auto-refresh every 2s</div>
            </div>
            <pre className="scroll-thin overflow-auto p-4 text-[12.5px] leading-6 font-mono whitespace-pre code-surface rounded-none border-0" style={{ maxHeight: 480 }}>
              {logsData?.logs || "(no logs yet — deploy the site to generate output)"}
            </pre>
          </Card>
        </TabsContent>

        <TabsContent value="config" className="mt-4 space-y-4">
          <Card className="p-3 text-sm border-border"
                style={{ background: "hsl(210 40% 12%)", borderColor: "hsl(210 40% 26%)", color: "hsl(210 40% 88%)" }}>
            <div className="flex items-start gap-2">
              <FileCode className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                Sites are exposed to Caddy via a stable loopback port on the VPS
                <code className="mx-1 font-mono">127.0.0.1:{config?.host_port || "…"}</code>
                (not reachable from the public internet). Caddy reverse-proxies
                <code className="mx-1 font-mono">{config?.full_host || fullHost}</code>
                to that loopback port and handles TLS.
                {" "}
                <b>If you changed base domain or upgraded emhost</b>, click <i>Regenerate configs</i>, then <i>Deploy</i>.
              </div>
            </div>
          </Card>
          <div className="flex justify-end">
            <Button
              variant="secondary"
              size="sm"
              className="gap-2"
              data-testid="site-regenerate-configs-button"
              onClick={async () => {
                try {
                  await api.post(`/sites/${site.id}/regenerate-configs`);
                  toast.success("Configs regenerated with current base domain");
                  refetchSite();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "regenerate failed");
                }
              }}
            >
              <RefreshCw className="h-3.5 w-3.5" /> Regenerate configs
            </Button>
          </div>
          {config?.stack_type === "fullstack_farm" ? (
            <>
              <CodeBlock label="Dockerfile.backend" language="dockerfile"
                code={config?.dockerfile} testid="site-config-dockerfile-copy-button" />
              <CodeBlock label="Dockerfile.frontend" language="dockerfile"
                code={config?.dockerfile_frontend} testid="site-config-dockerfile-frontend-copy-button" />
            </>
          ) : (
            <CodeBlock label="Dockerfile" language="dockerfile"
              code={config?.dockerfile} testid="site-config-dockerfile-copy-button" />
          )}
          <CodeBlock label="docker-compose.yml" language="yaml"
            code={config?.compose_yaml} testid="site-config-compose-copy-button" />
          <CodeBlock label={`Caddy site block — ${config?.full_host || fullHost}`}
            language="caddyfile" code={config?.caddy_snippet}
            testid="site-config-caddyfile-copy-button" />
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <Card data-testid="site-history-panel" className="p-0 border-border overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border surface-1">
              <div className="flex items-center gap-2 text-sm">
                <History className="h-4 w-4" /> Deploy history
              </div>
              <div className="text-xs text-muted-foreground">latest 20</div>
            </div>
            {(!history || history.length === 0) ? (
              <div className="p-8 text-center text-sm text-muted-foreground">
                No deploys yet. Trigger a deploy from the top-right.
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {history.map((h) => {
                  const ok = h.status === "success";
                  const failed = h.status === "error";
                  const Icon = ok ? CheckCircle2 : (failed ? XCircle : History);
                  const color = ok ? "text-emerald-400" : (failed ? "text-red-400" : "text-muted-foreground");
                  const ts = h.ts ? new Date(h.ts) : null;
                  return (
                    <li key={h.id} className="px-4 py-3 flex items-center gap-3">
                      <Icon className={`h-4 w-4 ${color}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate">{h.title}</div>
                        {h.message && <div className="text-[12px] text-muted-foreground font-mono truncate">{h.message}</div>}
                      </div>
                      <div className="text-[11px] text-muted-foreground font-mono">
                        {ts ? ts.toLocaleString() : ""}
                        {h.duration_ms != null && <span className="ml-2">{Math.round(h.duration_ms)}ms</span>}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="danger" className="mt-4">
          <Card className="p-6 border-border" style={{ borderColor: "hsl(0 60% 30%)" }}>
            <h2 className="text-lg font-semibold">Delete site</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Removes the container(s), workspace files, and all metadata. This cannot be undone.
            </p>
            <div className="mt-4">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button data-testid="site-danger-delete-button" variant="destructive" className="gap-2">
                    <Trash2 className="h-4 w-4" /> Delete site
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this site?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently delete {site.name} and its files.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={del} data-testid="site-danger-confirm-delete">
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Row({ k, v, mono }) {
  return (
    <div className="flex items-center justify-between gap-2 border-b border-border last:border-b-0 pb-2 last:pb-0">
      <span className="text-muted-foreground">{k}</span>
      <span className={mono ? "font-mono text-xs truncate max-w-[65%]" : ""}>{v ?? "—"}</span>
    </div>
  );
}

function EnvTab({ site, onSaved }) {
  const { data: servers } = useServers();
  const initial = useMemo(() => {
    const e = site.env || {};
    const rows = Object.entries(e).map(([k, v]) => ({ k, v }));
    return rows.length ? rows : [{ k: "", v: "" }];
  }, [site.env]);
  const [rows, setRows] = useState(initial);
  const [name, setName] = useState(site.name);
  const [sub, setSub] = useState(site.subdomain);
  const [custom, setCustom] = useState(site.custom_domain || "");
  const [serverId, setServerId] = useState(site.server_id || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const env = {};
    rows.forEach(({ k, v }) => { if (k.trim()) env[k.trim()] = v; });
    setSaving(true);
    try {
      await api.patch(`/sites/${site.id}`, {
        name, subdomain: sub, custom_domain: custom || null,
        server_id: serverId || null, env,
      });
      toast.success("Saved");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6 border-border">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-1.5">
          <Label>Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label>Subdomain</Label>
          <Input value={sub} onChange={(e) => setSub(e.target.value)} className="font-mono" />
        </div>
        <div className="space-y-1.5">
          <Label>Custom domain (optional)</Label>
          <Input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="app.mydomain.com" className="font-mono" />
        </div>
      </div>

      <div className="mt-4 space-y-1.5">
        <Label>Deploy target</Label>
        <select
          value={serverId}
          onChange={(e) => setServerId(e.target.value)}
          className="w-full h-10 rounded-md border border-input bg-transparent px-3 text-sm"
          data-testid="site-env-server-select"
        >
          <option value="">Simulation (no server)</option>
          {(servers || []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.name} — {s.username}@{s.host}:{s.port}{s.reachable ? " ✓" : " (unreachable)"}
            </option>
          ))}
        </select>
        <div className="text-[11px] text-muted-foreground">Changing the target requires re-deploying to take effect.</div>
      </div>

      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <Label>Environment variables</Label>
          <Button variant="ghost" size="sm" className="gap-1.5"
                  onClick={() => setRows((r) => [...r, { k: "", v: "" }])}
                  data-testid="site-env-add-button">
            <Plus className="h-3.5 w-3.5" /> Add
          </Button>
        </div>
        <div className="space-y-2" data-testid="site-env-editor">
          {rows.map((row, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2">
              <Input
                placeholder="KEY"
                value={row.k}
                onChange={(e) => setRows((r) => r.map((x, i) => i === idx ? { ...x, k: e.target.value } : x))}
                className="col-span-5 font-mono"
              />
              <Input
                placeholder="value"
                value={row.v}
                onChange={(e) => setRows((r) => r.map((x, i) => i === idx ? { ...x, v: e.target.value } : x))}
                className="col-span-6 font-mono"
              />
              <Button variant="ghost" size="icon" className="col-span-1"
                      onClick={() => setRows((r) => r.length === 1 ? [{ k: "", v: "" }] : r.filter((_, i) => i !== idx))}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 flex items-center justify-end gap-2">
        <Button data-testid="site-env-save-button" onClick={save} disabled={saving} className="gap-2">
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </Card>
  );
}

function RedeployButton({ siteId, onDone }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".zip")) {
      toast.error("Please select a .zip file");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/sites/${siteId}/redeploy`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Redeploy started");
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "redeploy failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <input ref={ref} type="file" accept=".zip" className="hidden"
             onChange={(e) => upload(e.target.files?.[0])}
             data-testid="site-redeploy-file-input" />
      <Button variant="secondary" className="gap-2" onClick={() => ref.current?.click()}
              disabled={busy} data-testid="site-redeploy-button">
        <UploadCloud className="h-4 w-4" /> {busy ? "Uploading…" : "Redeploy from new ZIP"}
      </Button>
    </>
  );
}


function CustomDomainCard({ site, onChanged }) {
  const [domain, setDomain] = useState("");
  const [busy, setBusy] = useState(false);
  const [detachBusy, setDetachBusy] = useState(false);
  const [result, setResult] = useState(null);

  const attach = async (overwrite = false) => {
    const d = (domain || "").trim().toLowerCase();
    if (!d || !d.includes(".")) {
      toast.error("Enter a full domain like example.com");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post(`/sites/${site.id}/attach-domain`,
        { domain: d, overwrite });
      setResult(data);
      const ok = data?.cloudflared?.ok !== false && data?.caddy?.ok !== false;
      if (ok) toast.success(`Attached ${d}${overwrite ? " (overwrote existing DNS)" : ""}`);
      else toast.warning(`Attached ${d} with warnings — see below`);
      setDomain("");
      onChanged?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // 409 with needs_overwrite → confirm and retry
      if (e?.response?.status === 409 && typeof detail === "object" && detail?.needs_overwrite) {
        const confirmed = window.confirm(
          `A DNS record for '${detail.hostname || d}' already exists in Cloudflare.\n\n` +
          `This is typically because the domain was previously pointing at a ` +
          `different VPS or tunnel. Overwriting will replace it with a CNAME ` +
          `to THIS server's tunnel.\n\n` +
          `Overwrite the existing DNS record?`
        );
        if (confirmed) {
          setBusy(false);
          return attach(true);
        }
      } else {
        toast.error(
          typeof detail === "string" ? detail :
          detail?.message ||
          e?.message ||
          "attach failed"
        );
      }
    } finally {
      setBusy(false);
    }
  };

  const detach = async () => {
    setDetachBusy(true);
    try {
      await api.post(`/sites/${site.id}/detach-domain`);
      toast.success("Custom domain detached");
      setResult(null);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "detach failed");
    } finally {
      setDetachBusy(false);
    }
  };

  return (
    <div data-testid="site-custom-domain-card">
      <div className="flex items-center gap-2">
        <Globe className="h-5 w-5" />
        <h2 className="text-lg font-semibold">Custom domain</h2>
      </div>
      <p className="text-sm text-muted-foreground mt-1">
        Attach your own domain (e.g. <code className="font-mono">mysite.com</code>) so visitors can
        reach this site under your brand. Works via Cloudflare Tunnel — no port-forwarding or SSL
        setup needed on your side.
      </p>

      {site.custom_domain ? (
        <div className="mt-4 p-3 rounded-md border border-border flex items-center justify-between gap-3"
             style={{ background: "hsl(210 40% 12%)" }}>
          <div className="flex items-center gap-3 min-w-0">
            <CheckCircle2 className="h-5 w-5 shrink-0" style={{ color: "hsl(142 71% 65%)" }} />
            <div className="min-w-0">
              <div className="font-mono text-sm truncate">{site.custom_domain}</div>
              <a href={`https://${site.custom_domain}`} target="_blank" rel="noreferrer"
                 className="text-xs text-primary inline-flex items-center gap-1 mt-0.5">
                Open <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
          <Button variant="secondary" size="sm" className="gap-1.5"
                  onClick={detach} disabled={detachBusy}
                  data-testid="site-detach-domain-button">
            {detachBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2Off className="h-3.5 w-3.5" />}
            Detach
          </Button>
        </div>
      ) : (
        <>
          <ol className="mt-3 space-y-1.5 text-sm text-muted-foreground list-decimal pl-5">
            <li>Add your domain to <b>Cloudflare</b> (Free plan is fine) and update the nameservers at your registrar. Wait until Cloudflare shows the zone as <i>Active</i>.</li>
            <li>Enter the full domain below and click <b>Attach</b>. emhost will automatically add the DNS record on Cloudflare and update the Caddy config on your VPS.</li>
            <li>Wait ~30–60 seconds for Cloudflare&apos;s edge to issue the SSL certificate, then open the domain in your browser.</li>
          </ol>

          <div className="mt-4 flex gap-2 flex-wrap items-end">
            <div className="flex-1 min-w-[240px]">
              <Label htmlFor="cd-input">Domain (FQDN)</Label>
              <Input
                id="cd-input"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") attach(false); }}
                placeholder="mysite.com or portfolio.example.io"
                data-testid="site-attach-domain-input"
              />
            </div>
            <Button onClick={() => attach(false)} disabled={busy || !domain.trim()}
                    className="gap-1.5"
                    data-testid="site-attach-domain-button">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowRight className="h-3.5 w-3.5" />}
              Attach
            </Button>
          </div>
        </>
      )}

      {result && (
        <div className="mt-4 space-y-2">
          {result.next_steps && result.next_steps.length > 0 && result.next_steps.map((s, i) => (
            <div key={i} className="text-sm rounded-md p-3 border"
                 style={{ background: "hsl(210 40% 12%)", borderColor: "hsl(210 40% 26%)", color: "hsl(210 40% 88%)" }}>
              {s}
            </div>
          ))}
          {result.cloudflared && result.cloudflared.log && result.cloudflared.log.length > 0 && (
            <CodeBlock label="cloudflared route dns output"
                       code={(result.cloudflared.log || []).join("\n")}
                       maxHeight="140px" />
          )}
        </div>
      )}
    </div>
  );
}


function MigrateSiteCard({ site, onChanged }) {
  const { data: servers } = useServers();
  const [open, setOpen] = useState(false);
  const [targetId, setTargetId] = useState("");
  const [transferVolumes, setTransferVolumes] = useState(true);
  const [updateCf, setUpdateCf] = useState(true);
  const [stopSource, setStopSource] = useState(false);
  const [busy, setBusy] = useState(false);
  const [migId, setMigId] = useState(null);
  const [mig, setMig] = useState(null);
  const logEndRef = useRef(null);

  const currentServer = (servers || []).find((s) => s.id === site.server_id);
  const otherServers = (servers || []).filter((s) => s.id !== site.server_id);

  // Poll migration status every 2s while it's queued/running
  const shouldPoll = migId && (!mig || mig.status === "queued" || mig.status === "running");
  useSWR(shouldPoll ? `/migrations/${migId}` : null,
    async (url) => (await api.get(url)).data,
    {
      refreshInterval: 2000,
      onSuccess: (data) => {
        setMig(data);
        setTimeout(() => {
          logEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
        }, 50);
      },
    }
  );

  const start = async () => {
    if (!targetId) {
      toast.error("Choose a target server");
      return;
    }
    setBusy(true);
    setMig(null);
    setMigId(null);
    try {
      const { data } = await api.post(`/sites/${site.id}/migrate`, {
        target_server_id: targetId,
        transfer_volumes: transferVolumes,
        update_cloudflare_route: updateCf,
        stop_source: stopSource,
      });
      setMig(data);
      setMigId(data.id);
      toast.success("Migration started — this may take several minutes");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "migration failed to start");
    } finally {
      setBusy(false);
    }
  };

  const closeDialog = () => {
    if (mig && (mig.status === "queued" || mig.status === "running")) {
      // still running - don't close accidentally, but allow
      if (!window.confirm("A migration is still in progress. Close this dialog anyway? It will keep running in the background.")) {
        return;
      }
    }
    setOpen(false);
    if (mig?.status === "success") {
      onChanged?.();
      // reset state on next open
      setMig(null);
      setMigId(null);
      setTargetId("");
    }
  };

  const running = mig && (mig.status === "queued" || mig.status === "running");
  const done = mig && mig.status === "success";
  const failed = mig && mig.status === "failed";

  return (
    <div data-testid="site-migrate-card">
      <div className="flex items-center gap-2">
        <ArrowRightLeft className="h-5 w-5" />
        <h2 className="text-lg font-semibold">Migrate to another server</h2>
      </div>
      <p className="text-sm text-muted-foreground mt-1">
        Move this site (source, config, and docker volumes) to a different registered VPS.
        Progress and databases are preserved. The source VPS is left untouched by default so
        you can roll back if something goes wrong.
      </p>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <div className="text-sm inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5"
             style={{ background: "hsl(210 40% 12%)" }}>
          <ServerIcon className="h-3.5 w-3.5" />
          <span className="text-muted-foreground">Current:</span>
          <span className="font-mono">{currentServer?.name || site.server_id}</span>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              variant="secondary"
              className="gap-1.5"
              disabled={otherServers.length === 0}
              data-testid="site-migrate-open-button"
            >
              <ArrowRightLeft className="h-3.5 w-3.5" />
              Migrate…
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ArrowRightLeft className="h-5 w-5" />
                Migrate {site.name}
              </DialogTitle>
              <DialogDescription>
                Moves the site to a different VPS. Source data is kept on the current
                server so you can roll back or verify.
              </DialogDescription>
            </DialogHeader>

            {!mig && (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label>Target server</Label>
                  <select
                    value={targetId}
                    onChange={(e) => setTargetId(e.target.value)}
                    className="w-full h-10 rounded-md border border-input bg-transparent px-3 text-sm"
                    data-testid="site-migrate-target-select"
                  >
                    <option value="">— select a server —</option>
                    {otherServers.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name} — {s.username}@{s.host}:{s.port}{s.reachable ? " ✓" : " (unreachable)"}
                      </option>
                    ))}
                  </select>
                  <div className="text-[11px] text-muted-foreground">
                    Target must be bootstrapped (docker + caddy + webnet). Bootstrap
                    from the Servers page if unsure.
                  </div>
                </div>

                <div className="space-y-2 border border-border rounded-md p-3"
                     style={{ background: "hsl(210 40% 10%)" }}>
                  <label className="flex items-start gap-2 text-sm cursor-pointer"
                         data-testid="site-migrate-transfer-volumes">
                    <Checkbox
                      checked={transferVolumes}
                      onCheckedChange={(v) => setTransferVolumes(!!v)}
                      className="mt-0.5"
                    />
                    <div>
                      <div className="font-medium">Transfer docker volumes (data / progress)</div>
                      <div className="text-xs text-muted-foreground">
                        Streams each detected volume (e.g. mongo, postgres, uploads) from
                        source → target via SFTP. Turn off for a clean install.
                      </div>
                    </div>
                  </label>

                  <label className="flex items-start gap-2 text-sm cursor-pointer"
                         data-testid="site-migrate-update-cf">
                    <Checkbox
                      checked={updateCf}
                      onCheckedChange={(v) => setUpdateCf(!!v)}
                      className="mt-0.5"
                    />
                    <div>
                      <div className="font-medium">Update Cloudflare Tunnel DNS route</div>
                      <div className="text-xs text-muted-foreground">
                        Runs <code className="font-mono">cloudflared tunnel route dns</code> on
                        the target so the site&apos;s hostname points to the new tunnel.
                        Only meaningful if you use CF Tunnel.
                      </div>
                    </div>
                  </label>

                  <label className="flex items-start gap-2 text-sm cursor-pointer"
                         data-testid="site-migrate-stop-source">
                    <Checkbox
                      checked={stopSource}
                      onCheckedChange={(v) => setStopSource(!!v)}
                      className="mt-0.5"
                    />
                    <div>
                      <div className="font-medium">Stop the container on source afterwards</div>
                      <div className="text-xs text-muted-foreground">
                        Data is preserved either way — this just stops the old container
                        to save resources. Leave off to keep it running in parallel.
                      </div>
                    </div>
                  </label>
                </div>

                <div className="text-xs rounded-md p-3 border"
                     style={{ background: "hsl(35 92% 12%)", borderColor: "hsl(35 92% 30%)",
                              color: "hsl(33 100% 88%)" }}>
                  <div className="flex items-start gap-2">
                    <Info className="h-4 w-4 mt-0.5 shrink-0" />
                    <div>
                      The site will briefly be unavailable while the target deploys and
                      volumes restore (typically 1–5 minutes, longer for big databases).
                      A retry-safe log is kept below.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {mig && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Status:</span>
                  {running && <>
                    <Loader2 className="h-4 w-4 animate-spin" style={{ color: "hsl(210 90% 65%)" }} />
                    <span style={{ color: "hsl(210 90% 78%)" }}>Running — phase: <code className="font-mono">{mig.phase}</code></span>
                  </>}
                  {done && <>
                    <CheckCircle2 className="h-4 w-4" style={{ color: "hsl(142 71% 65%)" }} />
                    <span style={{ color: "hsl(142 71% 78%)" }}>Success</span>
                  </>}
                  {failed && <>
                    <XCircle className="h-4 w-4" style={{ color: "hsl(0 90% 65%)" }} />
                    <span style={{ color: "hsl(0 90% 78%)" }}>Failed — {mig.error || "see log"}</span>
                  </>}
                </div>

                <pre
                  className="scroll-thin overflow-auto p-3 text-[11.5px] leading-5 font-mono whitespace-pre-wrap rounded-md border border-border"
                  style={{ background: "hsl(210 40% 8%)", maxHeight: 360, color: "hsl(210 20% 88%)" }}
                  data-testid="site-migrate-log"
                >
                  {(mig.log || []).join("\n")}
                  <div ref={logEndRef} />
                </pre>

                {done && (
                  <div className="text-sm rounded-md p-3 border"
                       style={{ background: "hsl(142 40% 10%)", borderColor: "hsl(142 40% 30%)",
                                color: "hsl(142 71% 88%)" }}>
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                      <div>
                        Migration complete. The site now runs on the target server. Visit
                        it to verify progress was preserved. Note: source container is
                        still running (data is safe there too) unless you chose to stop it.
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              {!mig && (
                <>
                  <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button
                    onClick={start}
                    disabled={busy || !targetId}
                    className="gap-1.5"
                    data-testid="site-migrate-start-button"
                  >
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowRightLeft className="h-3.5 w-3.5" />}
                    Start migration
                  </Button>
                </>
              )}
              {mig && !running && (
                <Button onClick={closeDialog} data-testid="site-migrate-close-button">
                  Close
                </Button>
              )}
              {running && (
                <Button variant="ghost" onClick={closeDialog}
                        data-testid="site-migrate-close-button">
                  Close (keeps running)
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {otherServers.length === 0 && (
        <div className="mt-3 text-xs text-muted-foreground">
          Register a second server on the <Link to="/servers" className="text-primary hover:underline">Servers page</Link> to
          enable migration.
        </div>
      )}
    </div>
  );
}
